import propertyUtils from '../property/property.utils.js';
import comparableUtils from '../propertyComparables/comparable.utils.js';
import rentSurveyUtils from '../propertyComparables/rentSurvey.utils';
import personasUtils from '../personas/personas.utils.js';
import computedOperationUtils from '@/services/common/computedOperation.utils';
import genericTabsUtils from '@/services/genericConfig/genericTabs.utils.js';

import genericTabConst from '@/consts/genericTab.const.js';
import Console from '@/shared/console';
import onEventFunctions from './onEventFunctions.utils.js';
import cssParseUtils from './cssParse.utils';
import evaluateUtils from '@/services/common/evaluate.utils';

export default {
  getFieldValue(metricKey, model, defaultValue, infixText, currentLang, computedOperation, featureSubIndex) {
    if (model) {
      let value = infixText || model[metricKey];

      if (value === undefined || value === null) {
        value = propertyUtils.getFieldValueFromProperty(model, metricKey, featureSubIndex);
      }

      if ((value === undefined || value === null) && computedOperation !== null && computedOperation !== undefined) {
        try {
          value = computedOperationUtils.getValueFromComputedOperation(model, metricKey, currentLang, computedOperation);

          if (Number.isNaN(value) || (typeof value !== 'string' && !Number.isFinite(value))) {
            value = null;
          }
        } catch (error) {
          console.log(error);
        }
      }

      if (value === undefined || value === null) {
        value = comparableUtils.getFieldValueFromComp(model, metricKey, currentLang);
      }

      if (value === undefined || value === null) {
        value = rentSurveyUtils.findMetricKeyInRentSurvey(model, metricKey);
      }

      if (value === undefined || value === null) {
        value = personasUtils.getValueInAttributesFromPerson(model, metricKey);
      }

      if (typeof value !== 'string' && value !== undefined && isNaN(value)) {
        value = defaultValue || null;
      }

      if (value === undefined || value === null) {
        value = defaultValue || null;
      }

      return value;
    }
  },

  toCamelCase(str) {
    if (typeof str === 'string') {
      return str.substring(0, 1).toLowerCase() + str.substring(1, str.length);
    }
    return str;
  },

  getSortableFieldTabKey(selectedTab) {
    if (genericTabsUtils.isLandTab(selectedTab)) {
      return 'LAND_COMPS_TAB';
    } else if (genericTabsUtils.isLeaseTab(selectedTab)) {
      return 'LEASE_COMPS_TAB';
    } else if (genericTabsUtils.isRentSurveyTab(selectedTab)) {
      return 'RENT_SURVEY_COMPS_TAB';
    } else if (genericTabsUtils.isSalesTab(selectedTab)) {
      return 'SALES_COMPS_TAB';
    } else if (selectedTab === genericTabConst.IE_COMPS) {
      return 'IE_COMPS_TAB';
    }

    return '';
  },

  roundNumber(value, numberDecimalsToRound) {
    if (value) {
      const numberDecimals = Math.pow(10, numberDecimalsToRound);
      return Math.round(value * numberDecimals) / numberDecimals;
    }
    return value;
  },

  sortByNumber(a, b) {
    if (Number(a) < Number(b)) {
      return -1;
    }
    if (Number(a) > Number(b)) {
      return 1;
    }
    return 0;
  },

  /**
   * @param {data} data Array of Filter objects
   */
  getFiltersStructure(filters) {
    return {
      LogicOperation: 'OR',
      Filters: [
        {
          LogicOperation: 'AND',
          Filters: filters.map(({ MetricKey, Values, MongoType, ParamId, ValueCode }) => ({
            Values: Array.isArray(Values) ? Values : [Values],
            Operation: {
              ParamId,
              ValueCode,
            },
            Field: {
              MetricKey,
              MongoType,
            },
          })),
        },
      ],
    };
  },

  getObjectFromKeysString(info) {
    const rootTranslations = {};

    info.forEach((element) => {
      const translation = {
        Route: element.InternalName,
        Translation: element.Translation,
      };
      const splittedRoute = translation.Route.split('.');
      let propertyReference = rootTranslations;
      splittedRoute.forEach((route, index) => {
        if (index < splittedRoute.length - 1) {
          if (!propertyReference[route]) {
            propertyReference[route] = {};
          }
          propertyReference = propertyReference[route];
        }
        if (index === splittedRoute.length - 1) {
          propertyReference[route] = translation.Translation;
        }
      });
    });

    return rootTranslations;
  },

  // Receives an object { user: { name: 'Gabe Newell' } } and a string 'user.name' and returns the value of the property ('Gabe Newell')
  getObjectValueFromString(obj, str) {
    let reference = null;
    str.split('.').forEach((node, index) => {
      if (index === 0) {
        reference = obj[node];
      } else {
        reference = reference[node];
      }
    });
    return reference;
  },

  parseDefaultValues(defaultValues) {
    if (!defaultValues) {
      return null;
    }
    const values = `${defaultValues}`.split(';');

    return values.map((value) => {
      if (value === '') return null;
      if (value === 'true') return true;
      if (value === 'false') return false;
      if (value === 'NULL') return null;
      return value;
    });
  },

  /**
   * These two methods are responsible for transforming the form data so that it is displayed in the form view and saved with the appropriate value.
   * It reads the values from the form and transforms them to display them correctly in the view.
   */
  readTransform(input, model = []) {
    const { FieldControlType, FieldDataType, Value, ParamId, ReadTransform, EmitEventName, shouldTransform, InternalField, FormId } = input;


    let transformedValue = Value;
    // The extra paramId check was removed because the MLA field was multiselect but the options where coming from the revenue units view and not from a paramlist
    // const isMultiselect = FieldControlType === 'multiselect' && ParamId && transformedValue !== null && !Array.isArray(transformedValue);
    const isMultiselect = FieldControlType === 'multiselect' && transformedValue !== null && !Array.isArray(transformedValue);
    const isStringParamList = FieldControlType === 'select' && ParamId && FieldDataType === 'string' && typeof transformedValue !== 'object';
    const isBoolean = FieldControlType === 'select' && ParamId && (typeof transformedValue === 'boolean' || typeof transformedValue === 'number' || FieldDataType === 'bool');
    const hasCustomTransform = FieldControlType === 'input' && EmitEventName;
    const hasReadTransform = !!ReadTransform;

    if (!hasReadTransform || !hasCustomTransform || !shouldTransform) {
      input.processedValue = '';
    }



    if (hasReadTransform) {
      transformedValue = transformedValue ? evaluateUtils.evaluateGenericExpression(ReadTransform, input, model, shouldTransform) : transformedValue;

      if (hasCustomTransform && shouldTransform) {
        input.processedValue = transformedValue;
        transformedValue = null;
        /**
         * En las siguientes lineas estamos obtienendo los inputs que ya tienen unas transformaciones especificas (EJ: blur, transformYearToMonths) y se esta seateando su valor original al Input como un atributo ya que este no se encuentra en el mismo.
         */
        const inputNode = document.querySelector(`#${cssParseUtils.saniticeCssSelector(InternalField)}-${FormId} input`);
        if (inputNode) {
          inputNode.dataset.originalValue = Value;
        }
      }

      if (['select'].includes(input.FieldControlType)) {
        input.Value = transformedValue;
      }
    }

    if (isMultiselect) {
      transformedValue = transformedValue.split(';');
    } else if (isStringParamList) {
      transformedValue = `${transformedValue}`;
    } else if (isBoolean) {
      transformedValue = this.castBoolToInt(transformedValue);
    }

    return transformedValue;
  },

  castBoolToInt(value) {
    // Casts true, 'true', 1 and '1' to '1'
    // Casts false, 'false', 0 and '0' to '0'
    // Otherwise casts to null and logs warning
    if (value === 'true' || value === true || value === 1 || value === '1') {
      return '1';
    } else if (value === 'false' || value === false || value === 0 || value === '0') {
      return '0';
    } else if (value === null) {
      return value;
    } else {
      Console.warn('Tried to parse a non bool');
      return null;
    }
  },

  configToEvents(input) {
    if (!input.EmitEventName) {
      return {};
    }
    const eventsArr = input.EmitEventName.split(';');

    return eventsArr.reduce((eventsObject, eventAndFunction) => {
      const [eventName, functionName] = eventAndFunction.split(':');
      return { ...eventsObject, [eventName]: () => onEventFunctions[functionName](input) };
    }, {});
  },

  setGoogleAutoCompleteDataInForm(location, formCopy) {
    const locationKeys = Object.keys(location);

    formCopy?.forEach((field) => {
      if (field.InitEventName && locationKeys?.includes(field.InitEventName)) {
        field.Value = location[field.InitEventName];
      }
    });

    return formCopy;
  },
};
